import React, { useState, useEffect } from 'react';
import styles from './DonatePage.module.css';
import { trackPageView, trackEvent } from '../utils/analytics'; // Import GA functions
import { useMediaQuery } from '../hooks/useMediaQuery';
import { stripeAPI } from '../api/apiService';
// For public assets, direct path from root is usually fine, or use process.env.PUBLIC_URL
// Given user feedback, using "/logo.png" directly in the src attribute is preferred.
// No import needed if using direct path in <img> src.

const presetAmounts = [18, 36, 180, 360];
const donationTargets = [
  'General',
  'Tzedaka',
  'Kollel',
  'Mikvah',
  'Building',
  'Seforim',
  'Farbrengen',
  'Pledge',
];

function DonatePage() {
  const [selectedAmount, setSelectedAmount] = useState(presetAmounts[0]);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustomAmountSelected, setIsCustomAmountSelected] = useState(false);

  const [selectedTarget, setSelectedTarget] = useState(donationTargets[0]);
  const [customTarget, setCustomTarget] = useState('');
  const [isCustomTargetSelected, setIsCustomTargetSelected] = useState(false);

  const [email, setEmail] = useState('');
  const [donationFrequency, setDonationFrequency] = useState('one-time'); // 'one-time' or 'recurring'

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // const [successMessage, setSuccessMessage] = useState(''); // For client-side success before redirect

  useEffect(() => {
    trackPageView('/donate'); // Track page view
  }, []);

  const handleAmountChange = (event) => {
    const value = parseInt(event.target.value, 10);
    setSelectedAmount(value);
    setCustomAmount('');
    setIsCustomAmountSelected(false);
  };

  const handleCustomAmountRadio = () => {
    setIsCustomAmountSelected(true);
    setSelectedAmount(0); // Or some indicator that custom is chosen
  };

  const handleCustomAmountChange = (event) => {
    setCustomAmount(event.target.value);
    if (event.target.value) {
        setSelectedAmount(0); // Clear preset if typing custom
        setIsCustomAmountSelected(true);
    }
  };

  const handleTargetChange = (event) => {
    const value = event.target.value;
    setSelectedTarget(value);
    setCustomTarget('');
    setIsCustomTargetSelected(false);
  };

  const handleCustomTargetRadio = () => {
    setIsCustomTargetSelected(true);
    setSelectedTarget('');
  };

  const handleCustomTargetChange = (event) => {
    setCustomTarget(event.target.value);
     if (event.target.value) {
        setSelectedTarget('');
        setIsCustomTargetSelected(true);
    }
  };

  const getOrigin = () => {
    // Handle test environment where window.location.origin might be undefined
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      return window.location.origin;
    }
    return 'http://localhost';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    const finalAmount = isCustomAmountSelected ? parseFloat(customAmount) : selectedAmount;
    const finalTarget = isCustomTargetSelected ? customTarget : selectedTarget;

    if (isNaN(finalAmount) || finalAmount <= 0) {
      setError('Please enter a valid donation amount.');
      setIsLoading(false);
      return;
    }
    if (!finalTarget.trim()) {
        setError('Please select or enter a donation target.');
        setIsLoading(false);
        return;
    }

    const amountInCents = Math.round(finalAmount * 100);
    const origin = getOrigin();

    // Track GA event for 'initiate_donation_attempt'
    trackEvent(
      'Donation', // Category
      'Initiate Donation Attempt', // Action
      finalTarget, // Label
      finalAmount // Value
    );

    const payload = {
      amountInCents,
      currency: 'usd',
      frequency: donationFrequency,
      recurringInterval: donationFrequency === 'recurring' ? 'month' : null,
      donationTarget: finalTarget,
      email: email,
      successUrl: `${origin}/donate/success`,
      cancelUrl: `${origin}/donate`,
    };

    try {
      console.log('Calling Stripe API "createCheckoutSession" with payload:', payload);
      const result = await stripeAPI.createCheckoutSession(payload);
      
      if (result && result.url) {
        window.location.assign(result.url);
      } else {
        throw new Error('Failed to get checkout session URL.');
      }
    } catch (err) {
      console.error('Error creating Stripe session:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  // Use media query hooks for responsive design
  const isMobile = useMediaQuery('(max-width: 480px)');
  const isTablet = useMediaQuery('(min-width: 481px) and (max-width: 768px)');

  return (
    <div className={styles.donatePageContainer}>
      <div className={styles.logoContainer}>
        <img src="/logo.png" alt="Shul Logo" className={styles.logo} />
      </div>
      <h1>Support BLY</h1>
      <form onSubmit={handleSubmit}>
        {/* Donation Amount */}
        <div className={styles.formGroup}>
          <div className={styles.amountOptionsGrid}>
            {presetAmounts.map((amount) => (
              <div 
                key={amount} 
                className={`${styles.radioOption} ${!isCustomAmountSelected && selectedAmount === amount ? styles.radioOptionSelected : ''}`}
                onClick={() => !isLoading && handleAmountChange({ target: { value: amount.toString() } })}
              >
                <input
                  type="radio"
                  id={`amount-${amount}`}
                  name="donationAmount"
                  value={amount}
                  checked={!isCustomAmountSelected && selectedAmount === amount}
                  onChange={handleAmountChange}
                  disabled={isLoading}
                />
                <label htmlFor={`amount-${amount}`}>${amount}</label>
              </div>
            ))}
            {/* "Other" amount button in the same row */}
            <div 
              className={`${styles.radioOption} ${isCustomAmountSelected ? styles.radioOptionSelected : ''}`}
              onClick={() => !isLoading && handleCustomAmountRadio()}
            >
              <input
                type="radio"
                id="amount-custom"
                name="donationAmount"
                value="custom"
                checked={isCustomAmountSelected}
                onChange={handleCustomAmountRadio}
                disabled={isLoading}
              />
              <label htmlFor="amount-custom">Other</label>
            </div>
          </div>
          {/* Custom amount input - only shows when "Other" is selected */}
          {isCustomAmountSelected && (
            <div style={{ marginTop: '0.75rem' }}>
              <input
                type="number"
                className={styles.otherAmountInput}
                placeholder="Enter amount"
                value={customAmount}
                onChange={handleCustomAmountChange}
                min="1"
                step="any"
                disabled={isLoading}
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Donation Target */}
        <div className={styles.formGroup}>
          <label>Donation Purpose</label>
          <div className={styles.targetOptionsGrid}>
            {donationTargets.map((target) => (
              <div 
                key={target} 
                className={`${styles.radioOption} ${!isCustomTargetSelected && selectedTarget === target ? styles.radioOptionSelected : ''}`}
                onClick={() => !isLoading && handleTargetChange({ target: { value: target } })}
              >
                <input
                  type="radio"
                  id={`target-${target.replace(/\s+/g, '-')}`}
                  name="donationTarget"
                  value={target}
                  checked={!isCustomTargetSelected && selectedTarget === target}
                  onChange={handleTargetChange}
                  disabled={isLoading}
                />
                <label htmlFor={`target-${target.replace(/\s+/g, '-')}`}>{target}</label>
              </div>
            ))}
          </div>
          
          {/* "Other" purpose option in the target grid */}
          <div 
            className={`${styles.radioOption} ${isCustomTargetSelected ? styles.radioOptionSelected : ''}`}
            onClick={() => !isLoading && handleCustomTargetRadio()}
          >
            <input
              type="radio"
              id="target-custom"
              name="donationTarget"
              value="custom"
              checked={isCustomTargetSelected}
              onChange={handleCustomTargetRadio}
              disabled={isLoading}
            />
            <label htmlFor="target-custom">Other</label>
          </div>
          
          {/* Custom target input - only shows when "Other" is selected */}
          {isCustomTargetSelected && (
            <div style={{ marginTop: '0.75rem' }}>
              <input
                type="text"
                className={styles.otherAmountInput}
                placeholder="Specify purpose"
                value={customTarget}
                onChange={handleCustomTargetChange}
                disabled={isLoading}
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Email for Receipt */}
        <div className={styles.formGroup}>
          <label htmlFor="email">Email (Optional - for receipt)</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            disabled={isLoading}
          />
        </div>

        {/* Donation Frequency */}
        <div className={styles.formGroup}>
          <label>Donation Frequency</label>
          <div className={styles.frequencyToggleContainer}>
            <div
              className={`${styles.frequencyOption} ${donationFrequency === 'one-time' ? styles.selected : ''}`}
              onClick={() => !isLoading && setDonationFrequency('one-time')}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => e.key === 'Enter' && !isLoading && setDonationFrequency('one-time')}
            >
              One Time
            </div>
            <div
              className={`${styles.frequencyOption} ${donationFrequency === 'recurring' ? styles.selected : ''}`}
              onClick={() => !isLoading && setDonationFrequency('recurring')}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => e.key === 'Enter' && !isLoading && setDonationFrequency('recurring')}
            >
              Monthly
            </div>
          </div>
        </div>

        {error && <p className={styles.errorMessage}>{error}</p>}

        <button type="submit" className={styles.submitButton} disabled={isLoading}>
          {isLoading ? 'Processing...' : 'Proceed to Donate'}
        </button>
      </form>
    </div>
  );
}

export default DonatePage;
